import CommentIcon from '@mui/icons-material/Comment';
import SearchIcon from '@mui/icons-material/Search';
import {
    Avatar,
    Box,
    Card,
    CardContent,
    Container,
    Grid,
    InputAdornment,
    TextField,
    ThemeProvider,
    Typography
} from '@mui/material';
import moment from "moment";
import { useSnackbar } from 'notistack';
import { Suspense, useEffect, useState } from "react";
import { useDispatch } from 'react-redux';
import { Route, Switch, useHistory, useRouteMatch } from "react-router-dom";
import ChangePasswordDialog, { ChangePasswordDialogData } from '../../components/Header/ChangePasswordDialog';
import ChangePhotoDialog, { ChangePhotoDialogData } from '../../components/Header/ChangePhotoDialog';
import ProfileMenu from '../../components/Header/ProfileMenu';
import HeaderLogo from '../../components/HeaderLogo';
import { OverlayLoading } from "../../components/OverlayLoading/OverlayLoading";
import { Unauthorized } from "../../components/PrivateRoute";
import UserPhoto from '../../components/UserPhoto';
import Task from '../../model/Task';
import User from '../../model/User';
import { logoutAction, successLoginAction, useAuthentication } from "../../reducer/authentication";
import { loadingAction } from '../../reducer/loading';
import AuthService from '../../service/AuthService';
import { WhereTree } from '../../service/ServiceApi';
import TaskApi from '../../service/TaskApi';
import UserApi from '../../service/UserApi';
import { getFirstLettersToAvatar } from '../../utils';
import { TaskPageRoot } from './TaskPage';
import { ThemeBlueDark } from '../../themes/ThemeBlue';

function SystemMobile() {
    const match = useRouteMatch();
    const { user } = useAuthentication();
    return (
        <ThemeProvider theme={ThemeBlueDark}>
            <Suspense fallback={<OverlayLoading loading />}>
                <Switch>
                    <Route path={match.path} exact>
                        <SystemMobileHome />
                    </Route>
                    <Route path={`${match.url}/task/:id(\\d+)`}><TaskPageRoot /></Route>
                    <Route>
                        <Unauthorized redirectUrl={false} />
                    </Route>
                </Switch>
            </Suspense>
        </ThemeProvider>
    );
}

export function SystemMobileHeader() {
    const { user, loggedIn } = useAuthentication();
    const [userMenuAnchorEl, setUserMenuAnchorEl] = useState<HTMLElement | null>(null);
    const [changePasswordDialogOpen, setChangePasswordDialogOpen] = useState<boolean>(false);
    const [changePhotoDialogOpen, setChangePhotoDialogOpen] = useState<boolean>(false);

    const { enqueueSnackbar } = useSnackbar();

    const dispatch = useDispatch();

    function handleProfileMenuOpen(event: React.MouseEvent<HTMLElement>) {
        setUserMenuAnchorEl(event.currentTarget);
    }

    function handleProfileMenuClose() {
        setUserMenuAnchorEl(null);
    }

    function loading(value: boolean) {
        dispatch(loadingAction(value));
    }

    function openChangePassword() {
        setChangePasswordDialogOpen(true);
        setUserMenuAnchorEl(null);
    }

    function openChangePhoto() {
        setChangePhotoDialogOpen(true);
        setUserMenuAnchorEl(null);
    }

    function closeChangePassword() {
        setChangePasswordDialogOpen(false);
    }

    function closeChangePhoto() {
        setChangePhotoDialogOpen(false);
    }

    async function logOut() {
        loading(true);
        await AuthService.instance.logout();
        loading(false);
        dispatch(logoutAction());
    }

    async function onSubmitChangePassowrd(data: ChangePasswordDialogData) {
        if (!user) return;
        const userApi = new UserApi();
        try {
            await userApi.changePassword(user.id, data.oldPassword, data.newPassword);
            enqueueSnackbar('Senha alterada com sucesso!', { variant: 'success' });
            closeChangePassword()
        } catch (e) {
            var errorMsg = e instanceof Error ? e.message : String(e);
            if (errorMsg.includes('Invalid Password')) {
                errorMsg = 'Senha invalida';
            }
            console.log(e);
            enqueueSnackbar(errorMsg, { variant: 'error' });
        }
    }

    async function onSubmitChangePhoto({ photo }: ChangePhotoDialogData) {
        if (!user || !(photo instanceof File)) return;
        const userApi = new UserApi();
        try {
            await userApi.uploadPhoto(photo, user.id);
            enqueueSnackbar('Foto alterada com sucesso!', { variant: 'success' });
            const userUpdated = await AuthService.instance.getAuthUser();
            dispatch(successLoginAction(userUpdated));
            closeChangePhoto();
        } catch (e) {
            var errorMsg = e instanceof Error ? e.message : String(e);
            console.log(e);
            enqueueSnackbar('Erro ao atualizar foto.', { variant: 'error' });
        }
    }

    const isMenuOpen = Boolean(userMenuAnchorEl);
    if (!user) return null;

    return <>
        <Box
            sx={{
                backgroundColor: '#f3f4f6',
                color: '#111827',
                px: 4,
                py: 2,
                display: 'flex',
                alignItems: 'center',
                borderBottom: '1px solid #ddd',
                height: 65,
                boxSizing: 'border-box'
            }}
        >
            <HeaderLogo />
            <Box sx={{ ml: 4, flex: 1 }}>
                <Typography variant="h6" fontWeight="bold">
                    SafeDesk
                </Typography>
                <Typography variant="body2">Gestão de chamados</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', position: 'relative', alignItems: 'center' }}>
                <Avatar sx={{ cursor: 'pointer', fontSize: 22, margin: 1 }} onClick={handleProfileMenuOpen}>
                    {user?.photo ? <UserPhoto key={user.photo} id={user.id} photo={user.photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getFirstLettersToAvatar(user?.name || '')}
                </Avatar>
            </Box>
        </Box>
        <ProfileMenu
            user={user}
            isOpen={isMenuOpen}
            anchorEl={userMenuAnchorEl}
            onClose={handleProfileMenuClose}
            onLogOut={logOut}
            openChangePassword={openChangePassword}
            openChangePhoto={openChangePhoto}
        />
        <ChangePasswordDialog
            changePasswordDialogOpen={changePasswordDialogOpen}
            onCancel={closeChangePassword}
            onSubmit={onSubmitChangePassowrd}
        />
        <ChangePhotoDialog
            changePhotoDialogOpen={changePhotoDialogOpen}
            onCancel={closeChangePhoto}
            onSubmit={onSubmitChangePhoto}
        />
    </>;
}

const taskApi = new TaskApi();

function TaskCard({ task }: { task: Task }) {
    const history = useHistory();
    return <Box
        sx={{
            display: 'flex',
            alignItems: 'start',
            backgroundColor: '#1f2937',
            borderRadius: 2,
            p: 2,
            mt: 3,
        }}
        onClick={() => {
            history.push(`/system/task/${task.id}`);
        }}
    >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255, 0.1)', mr: 2, width: 40, height: 40, color: '#fff', fontSize: '14px', fontWeight: 600 }}>
            {task.user?.photo ? <UserPhoto key={task.user.photo} id={task.user.id} photo={task.user.photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getFirstLettersToAvatar(task.user?.name || '')}
        </Avatar>
        <Box sx={{
            gap: 1,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Typography variant="body1" fontWeight="bold">
                {task.name} - {moment(task.created_at).format('DD/MM/YYYY')}
            </Typography>
            <Typography variant="body2" color="gray">
                Status: {task.ticket_status?.name || '-'}
            </Typography>
        </Box>
    </Box>
}

function CommentCard({ task, user }: { task: Task, user: User }) {
    const history = useHistory();
    const hasSomeCommentAwaredNull = task.comments?.some(comment => comment.comment_task_users?.some(ctu => ctu.awared_at === null && ctu.user_id === user.id));

    return <Box
        sx={{
            display: 'flex',
            alignItems: 'start',
            backgroundColor: !!hasSomeCommentAwaredNull ? '#1f1722' : '#1f1f1a',
            border: !!hasSomeCommentAwaredNull ? '1px solid #3b1e2e' : '1px solid #3b3a1e',
            borderRadius: 2,
            p: 2,
            mt: 3,
        }}
        onClick={() => {
            history.push(`/system/task/${task.id}`);
        }}
    >
        <Avatar sx={{ bgcolor: 'rgba(255,255,255, 0.1)', mr: 2, width: 40, height: 40, color: '#fff', fontSize: '14px', fontWeight: 600 }}>
            {task.user?.photo ? <UserPhoto key={task.user.photo} id={task.user.id} photo={task.user.photo} style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : getFirstLettersToAvatar(task.user?.name || '')}
        </Avatar>
        <Box sx={{
            gap: 1,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Typography variant="body1" fontWeight="bold">
                {task.name}
            </Typography>
            <Typography variant="body2" color="gray">
                Status: {task.ticket_status?.name || '-'}
            </Typography>
        </Box>
    </Box>
}

function SystemMobileHome() {
    const { user } = useAuthentication();
    const [tasks, setTasks] = useState<Task[]>([]);
    const [comments, setComments] = useState<Task[]>([]);
    const [filter, setFilter] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            const whereTask: WhereTree<Task> = {
                operator: 'and',
                conditions: []
            };
            whereTask.conditions.push({ field: `ticket_status.closed`, op: '=', value: false });
            whereTask.conditions.push({ field: `user_id`, op: '=', value: user?.id });
            const taskList = await taskApi.getList({ whereTree: whereTask, relations: ['user', 'ticket_status'], order: { 'id': 'DESC' } });

            const whereComment: WhereTree<Task> = {
                operator: 'and',
                conditions: []
            };
            whereComment.conditions.push({ field: `comments.canceled`, op: '=', value: false });
            whereComment.conditions.push({ field: `comments.comment_task_users.user_id`, op: '=', value: user?.id });
            whereComment.conditions.push({ field: `comments.comment_task_users.resolved_at`, op: '=', value: null });
            const commentList = await taskApi.getList({ whereTree: whereComment, relations: ['user', 'ticket_status', 'comments', 'comments.comment_task_users'], order: { 'id': 'DESC' } });

            setComments(commentList);
            setTasks(taskList);
        };
        fetchData();
    }, []);

    const filteredTasks = tasks.filter(task => task.name.toLowerCase().includes(filter.toLowerCase()));

    if (!user) return null;

    return (
        <Box sx={{ overflow: 'auto', height: '100%', backgroundColor: '#0a0e17', color: '#fff' }}>
            <SystemMobileHeader />
            <Container sx={{ pt: 4, pb: 4 }}>
                <TextField
                    fullWidth
                    placeholder="Filtrar chamados..."
                    variant="outlined"
                    size="small"
                    sx={{ input: { color: '#fff' }, mb: 3 }}
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#888' }} />
                            </InputAdornment>
                        ),
                        sx: {
                            backgroundColor: '#111827',
                            borderRadius: 2,
                            color: '#fff',
                        },
                    }}
                />

                <Grid container spacing={4}>
                    {/* Meus Chamados */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ backgroundColor: 'transparent', backgroundImage: 'none', border: '1px solid #1f2937', borderRadius: 2 }}>
                            <CardContent style={{ paddingBottom: 16 }}>
                                <Typography variant="h6" gutterBottom>
                                    <CommentIcon fontSize="small" sx={{ mr: 1, mb: '-3px' }} />
                                    Meus Chamados
                                </Typography>
                                {filteredTasks.length > 0 ? filteredTasks.map((task) => (
                                    <TaskCard key={task.id} task={task} />
                                )) : (
                                    <Typography variant="body2" color="gray" sx={{ mt: 2 }}>
                                        Nenhum chamado.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Comentários para Mim */}
                    <Grid item xs={12} md={6}>
                        <Card sx={{ backgroundColor: 'transparent', backgroundImage: 'none', border: '1px solid #1f2937', borderRadius: 2 }}>
                            <CardContent style={{ paddingBottom: 16 }}>
                                <Typography variant="h6" gutterBottom>
                                    <CommentIcon fontSize="small" sx={{ mr: 1, mb: '-3px' }} />
                                    Comentários para Mim
                                </Typography>
                                {comments.length > 0 ? comments.map((task) => (
                                    <CommentCard key={task.id} task={task} user={user} />
                                )) : (
                                    <Typography variant="body2" color="gray" sx={{ mt: 2 }}>
                                        Nenhum comentário.
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};


export default SystemMobile;